import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Card, Col, Input, Row, Select, Skeleton, Spin, Typography, message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  getPatientHistoryByPin,
  getPatientHistory,
  searchPatients,
} from "../../services/doctorService";
import { useModules } from "../../context/ModuleContext";
import PatientHistoryView from "./PatientHistoryView";

const { Title, Text } = Typography;

const formatPatientLabel = (p) => {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  const pinLabel = p.pin ? `PIN ${p.pin}` : "";
  return [name || p.email, pinLabel].filter(Boolean).join(" · ");
};

export default function PatientLookup() {
  const { hasFullAccess, organisation } = useModules();

  const [pin, setPin]                           = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientOptions, setPatientOptions]     = useState([]);
  const [searching, setSearching]               = useState(false);
  const [hasSearched, setHasSearched]           = useState(false);
  const [searchTerm, setSearchTerm]             = useState("");
  const [loading, setLoading]                   = useState(false);
  const [loadError, setLoadError]               = useState(null);
  const [data, setData]                         = useState(null);

  const debounceRef  = useRef();
  const lastLookupRef = useRef(null);

  const isPartnerOrg = !hasFullAccess && Boolean(organisation);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const runSearch = async (term) => {
    setSearching(true);
    setHasSearched(true);
    setSearchTerm(term || "");
    try {
      const res = await searchPatients(term || "");
      setPatientOptions(res.data || []);
    } catch (err) {
      message.error("Patient search failed. Please check your connection and try again.");
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (term) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), 300);
  };

  const handleLookupByPin = async () => {
    const clean = (pin || "").trim();
    if (!/^\d{8}$/.test(clean)) {
      message.error("Enter a valid 8-digit patient PIN.");
      return;
    }
    lastLookupRef.current = handleLookupByPin;
    setLoading(true);
    setLoadError(null);
    setData(null);
    try {
      const res = await getPatientHistoryByPin(clean);
      setData(res.data);
    } catch (err) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 403) {
        setLoadError("You can only access medical history for patients in your organisation.");
      } else if (httpStatus === 404) {
        setLoadError(`No patient found with PIN ${clean}. Please verify the PIN and try again.`);
      } else {
        setLoadError("Unable to load patient history. Please check your connection and try again. If the problem persists, contact support.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPatient = async (patientId) => {
    setSelectedPatientId(patientId);
    if (!patientId) { setData(null); setLoadError(null); return; }
    lastLookupRef.current = () => handleSelectPatient(patientId);
    const patient = patientOptions.find((p) => p.id === patientId);
    setLoading(true);
    setLoadError(null);
    setData(null);
    try {
      const profileId = patient?.patient_profile_id ?? patientId;
      const res = patient?.pin
        ? await getPatientHistoryByPin(patient.pin)
        : await getPatientHistory(profileId);
      setData(res.data);
    } catch (err) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 403) {
        setLoadError("You can only access medical history for patients in your organisation.");
      } else {
        setLoadError("Unable to load patient history. Please check your connection and try again. If the problem persists, contact support.");
      }
    } finally {
      setLoading(false);
    }
  };

  const options = patientOptions.map((p) => ({
    value: p.id,
    label: formatPatientLabel(p),
  }));

  return (
    <>
      <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>
        Medical History Lookup
      </Title>

      <Alert
        type="info"
        showIcon
        message="Access logged for HIPAA audit — Consent on file (paper form)"
        description={
          isPartnerOrg
            ? `Patients in ${organisation} have signed a paper consent authorizing doctors in this organisation to review their medical history for consultation purposes. Every lookup is recorded in the audit log.`
            : "Patients have signed a paper consent authorizing network doctors to review their medical history for consultation purposes. Every lookup is recorded in the audit log."
        }
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      <Card styles={{ body: { padding: 24 } }} style={{ borderRadius: 8, marginBottom: 16 }}>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={10}>
            <Text strong style={{ display: "block", marginBottom: 6 }}>Search by Patient PIN</Text>
            <Input.Search
              placeholder="8-digit PIN (e.g. 12345678)"
              enterButton={<span><SearchOutlined /> Look Up</span>}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              onSearch={handleLookupByPin}
              maxLength={8}
            />
          </Col>
          <Col xs={24} md={14}>
            <Text strong style={{ display: "block", marginBottom: 6 }}>Or search by name / email / PIN</Text>
            <Select
              showSearch
              allowClear
              filterOption={false}
              placeholder={
                isPartnerOrg
                  ? `Search patients in ${organisation}…`
                  : "Start typing a patient's name, email, or PIN…"
              }
              style={{ width: "100%" }}
              options={options}
              value={selectedPatientId}
              notFoundContent={
                searching
                  ? <Spin size="small" />
                  : hasSearched && patientOptions.length === 0
                    ? <Text type="secondary" style={{ fontSize: 12, padding: "4px 8px", display: "block" }}>
                        {searchTerm
                          ? `No patients found matching "${searchTerm}". Try searching by PIN, full name, or email address.`
                          : "No patients found. Try searching by PIN, full name, or email address."}
                      </Text>
                    : null
              }
              onSearch={handleSearchChange}
              onFocus={() => patientOptions.length === 0 && runSearch("")}
              onChange={handleSelectPatient}
            />
            {isPartnerOrg && (
              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                Showing patients in {organisation} only
              </Text>
            )}
          </Col>
        </Row>
      </Card>

      {loading ? (
        <Card styles={{ body: { padding: 24 } }} style={{ borderRadius: 8 }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : data ? (
        <Card styles={{ body: { padding: 24 } }} style={{ borderRadius: 8 }}>
          <PatientHistoryView data={data} />
        </Card>
      ) : loadError ? (
        <Card
          styles={{ body: { padding: 32, textAlign: "center" } }}
          style={{ borderRadius: 8, borderColor: "#ffccc7" }}
        >
          <Text type="danger">{loadError}</Text>
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Need help? Contact support at info@mchusa.com
            </Text>
          </div>
          {lastLookupRef.current && (
            <div style={{ marginTop: 12 }}>
              <Button size="small" onClick={() => lastLookupRef.current?.()}>Try Again</Button>
            </div>
          )}
        </Card>
      ) : (
        <Card
          styles={{ body: { padding: 32, textAlign: "center" } }}
          style={{ borderRadius: 8 }}
        >
          <Text type="secondary">Enter a PIN or select a patient to view their history.</Text>
        </Card>
      )}
    </>
  );
}
